// Time + timezone helpers for the public booking UI.
// All slot instants from the API are ISO-8601 UTC; we render them in the
// visitor's chosen IANA timezone (default: Intl auto-detected). Luxon does the
// zone math (DST-correct). Implements: architecture.md v1.0 (tz display-only).

import { DateTime } from 'luxon';

/** The visitor's auto-detected IANA timezone, with a safe fallback. */
export function detectTimeZone(): string {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    return 'UTC';
  }
}

/** Stable key for a calendar day in a given zone, e.g. '2026-06-24'. */
export function dayKey(isoUtc: string, tz: string): string {
  return DateTime.fromISO(isoUtc, { zone: 'utc' }).setZone(tz).toFormat('yyyy-LL-dd');
}

/** Short time, e.g. '09:00' (24h, tabular-friendly) in the chosen zone. */
export function formatTime(isoUtc: string, tz: string): string {
  return DateTime.fromISO(isoUtc, { zone: 'utc' }).setZone(tz).toFormat('HH:mm');
}

/**
 * Shift a UTC instant forward by `minutes`, returning a new ISO-8601 UTC string.
 * Returns the instant UNCHANGED when minutes <= 0. This is the DISPLAY-ONLY
 * transform for a booking type's front buffer: the human-facing surfaces (slot
 * labels, the details summary, the confirmation, the manage view) render the
 * shifted actual meeting start, while the value submitted to the server always
 * stays the nominal slot start. Pure UTC-instant math, so downstream timezone
 * rendering is unaffected.
 */
export function shiftInstant(isoUtc: string, minutes: number): string {
  if (minutes <= 0) return isoUtc;
  return DateTime.fromISO(isoUtc, { zone: 'utc' }).plus({ minutes }).toUTC().toISO() ?? isoUtc;
}

/** Day-of-week + day number for a day pill, e.g. { dow: 'Wed', day: '24' }. */
export function dayPillParts(isoUtc: string, tz: string): { dow: string; day: string; month: string } {
  const dt = DateTime.fromISO(isoUtc, { zone: 'utc' }).setZone(tz);
  return { dow: dt.toFormat('ccc'), day: dt.toFormat('d'), month: dt.toFormat('LLL') };
}

/** Long, human date for a slot, e.g. 'Wednesday, 24 June 2026'. */
export function formatLongDate(isoUtc: string, tz: string): string {
  return DateTime.fromISO(isoUtc, { zone: 'utc' })
    .setZone(tz)
    .toFormat('cccc, d LLLL yyyy');
}

/** Full local timestamp for a confirmation, e.g. '09:00 - 09:30'. */
export function formatTimeRange(startUtc: string, endUtc: string, tz: string): string {
  return `${formatTime(startUtc, tz)} - ${formatTime(endUtc, tz)}`;
}

/** A friendly zone label including the current offset, e.g. 'Europe/Zurich (GMT+2)'. */
export function zoneLabel(tz: string, refIsoUtc?: string): string {
  const dt = (refIsoUtc ? DateTime.fromISO(refIsoUtc, { zone: 'utc' }) : DateTime.utc()).setZone(tz);
  const offset = dt.toFormat('ZZ'); // e.g. +02:00
  return `${tz.replace(/_/g, ' ')} (GMT${offset})`;
}

/**
 * Human relative time for a PAST instant, e.g. '5 minutes ago' (Luxon
 * toRelative). Pinned to English like the rest of the app copy, whatever the
 * runtime locale. `nowIso` pins the base for tests; defaults to now. Empty
 * string on an unparseable input rather than throwing (display-only helper).
 */
export function relativeTime(isoUtc: string, nowIso?: string): string {
  const dt = DateTime.fromISO(isoUtc, { zone: 'utc' });
  if (!dt.isValid) return '';
  const base = nowIso ? DateTime.fromISO(nowIso, { zone: 'utc' }) : DateTime.utc();
  return dt.toRelative({ base, locale: 'en' }) ?? '';
}

/** ISO start/end of a rolling window from now, used for the availability query. */
export function rollingWindow(days: number, fromIso?: string): { from: string; to: string } {
  const start = fromIso ? DateTime.fromISO(fromIso, { zone: 'utc' }) : DateTime.utc();
  return {
    from: start.toUTC().toISO() ?? new Date().toISOString(),
    to: start.plus({ days }).toUTC().toISO() ?? new Date().toISOString(),
  };
}

/**
 * A curated list of common IANA zones for the timezone picker, always including
 * the supplied current zone (so the visitor's auto-detected zone is selectable
 * even if it is not in the curated set).
 */
export function commonTimeZones(current: string): string[] {
  const base = [
    'Europe/Zurich',
    'Europe/London',
    'Europe/Berlin',
    'Europe/Paris',
    'Europe/Madrid',
    'Europe/Athens',
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Sao_Paulo',
    'Asia/Dubai',
    'Asia/Kolkata',
    'Asia/Singapore',
    'Asia/Tokyo',
    'Australia/Sydney',
    'UTC',
  ];
  const set = new Set(base);
  set.add(current);
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Group slots by local calendar day, preserving ascending order. */
export function groupSlotsByDay<T extends { start: string }>(
  slots: T[],
  tz: string,
): { key: string; sample: string; slots: T[] }[] {
  const map = new Map<string, T[]>();
  for (const s of slots) {
    const key = dayKey(s.start, tz);
    const arr = map.get(key);
    if (arr) arr.push(s);
    else map.set(key, [s]);
  }
  return Array.from(map.entries()).map(([key, daySlots]) => ({
    key,
    sample: daySlots[0]!.start,
    slots: daySlots,
  }));
}
